<template>
  <el-card>
    <div slot="header" class="toolbar">
      <span>Redis管理</span>
      <el-button size="mini" @click="refreshAll">刷新</el-button>
    </div>

    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-label">Key数量</div>
        <div class="stat-value">{{ redisInfo.dbSize || 0 }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Redis版本</div>
        <div class="stat-value">{{ redisInfo.redisVersion || '-' }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">已用内存</div>
        <div class="stat-value">{{ redisInfo.usedMemoryHuman || '-' }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">连接客户端</div>
        <div class="stat-value">{{ redisInfo.connectedClients || 0 }}</div>
      </div>
    </div>

    <div class="search-bar">
      <el-input
        v-model.trim="query.pattern"
        placeholder="请输入Key匹配表达式，如 mall:*"
        clearable
        style="width: 320px"
        @keyup.enter.native="handleSearch"
      />
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="reset">重置</el-button>
    </div>

    <el-alert
      v-if="limited"
      title="当前最多展示前1000个匹配Key，请输入更精确的匹配表达式缩小范围。"
      type="warning"
      :closable="false"
      show-icon
      class="limit-alert"
    />

    <el-table v-loading="loading" :data="list" border style="width: 100%">
      <el-table-column prop="key" label="Key" min-width="260" show-overflow-tooltip />
      <el-table-column prop="type" label="类型" width="100" />
      <el-table-column label="TTL" width="130">
        <template slot-scope="{ row }">
          {{ ttlText(row.ttl) }}
        </template>
      </el-table-column>
      <el-table-column prop="size" label="大小/数量" width="110" />
      <el-table-column label="值预览" min-width="260" show-overflow-tooltip>
        <template slot-scope="{ row }">
          {{ valueText(row.valuePreview) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="230" fixed="right">
        <template slot-scope="{ row }">
          <el-button size="mini" @click="openDetail(row)">详情</el-button>
          <el-button
            v-if="$hasPermission('sys:redis:expire', 'sys:redis:list')"
            size="mini"
            type="primary"
            @click="openExpire(row)"
          >
            TTL
          </el-button>
          <el-button
            v-if="$hasPermission('sys:redis:delete', 'sys:redis:list')"
            size="mini"
            type="danger"
            @click="removeKey(row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-bar">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="query.page"
        :page-size="query.limit"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog title="Key详情" :visible.sync="detailVisible" width="760px">
      <el-descriptions v-if="detail.key" :column="2" border>
        <el-descriptions-item label="Key">{{ detail.key }}</el-descriptions-item>
        <el-descriptions-item label="类型">{{ detail.type }}</el-descriptions-item>
        <el-descriptions-item label="TTL">{{ ttlText(detail.ttl) }}</el-descriptions-item>
        <el-descriptions-item label="大小/数量">{{ detail.size }}</el-descriptions-item>
      </el-descriptions>
      <pre class="detail-content">{{ valueText(detail.value) }}</pre>
      <span slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog title="设置过期时间" :visible.sync="expireVisible" width="480px" :close-on-click-modal="false">
      <el-form label-width="120px">
        <el-form-item label="Key">
          <el-input :value="expireForm.key" disabled />
        </el-form-item>
        <el-form-item label="过期时间">
          <el-input-number v-model="expireForm.ttl" :min="0" :step="60" style="width: 100%" />
          <div class="form-tip">单位：秒。填写0表示永久不过期。</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="expireVisible = false">取消</el-button>
        <el-button type="primary" :loading="expireLoading" @click="submitExpire">保存</el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import http from '../../utils/http'

function defaultQuery () {
  return {
    page: 1,
    limit: 10,
    pattern: '*'
  }
}

export default {
  data () {
    return {
      loading: false,
      redisInfo: {},
      list: [],
      total: 0,
      limited: false,
      query: defaultQuery(),
      detailVisible: false,
      detail: {},
      expireVisible: false,
      expireLoading: false,
      expireForm: {
        key: '',
        ttl: 0
      }
    }
  },
  created () {
    this.refreshAll()
  },
  methods: {
    async refreshAll () {
      await Promise.all([this.loadInfo(), this.load()])
    },
    async loadInfo () {
      const { data } = await http.get('/sys/redis/info')
      this.redisInfo = data.data || {}
    },
    async load () {
      this.loading = true
      try {
        const { data } = await http.get('/sys/redis/keys', { params: this.query })
        const pageData = data.data || {}
        this.list = pageData.list || []
        this.total = Number(pageData.total || 0)
        this.limited = !!pageData.limited
      } finally {
        this.loading = false
      }
    },
    handleSearch () {
      this.query.page = 1
      this.load()
    },
    reset () {
      this.query = defaultQuery()
      this.load()
    },
    handlePageChange (page) {
      this.query.page = page
      this.load()
    },
    handleSizeChange (limit) {
      this.query.limit = limit
      this.query.page = 1
      this.load()
    },
    async openDetail (row) {
      const { data } = await http.get('/sys/redis/detail', { params: { key: row.key } })
      this.detail = data.data || {}
      this.detailVisible = true
    },
    openExpire (row) {
      this.expireForm = {
        key: row.key,
        ttl: Number(row.ttl || 0) > 0 ? Number(row.ttl) : 0
      }
      this.expireVisible = true
    },
    async submitExpire () {
      this.expireLoading = true
      try {
        const { data } = await http.post('/sys/redis/expire', this.expireForm)
        if (data.code === 0) {
          this.$message.success('过期时间设置成功')
          this.expireVisible = false
          await this.load()
        }
      } finally {
        this.expireLoading = false
      }
    },
    removeKey (row) {
      const message = `
        <div class="redis-delete-confirm">
          <div class="redis-delete-confirm__icon">!</div>
          <div class="redis-delete-confirm__body">
            <div class="redis-delete-confirm__title">确认删除这个 Redis Key？</div>
            <div class="redis-delete-confirm__key">${this.escapeHtml(row.key)}</div>
            <div class="redis-delete-confirm__tip">删除后无法恢复，请确认该 Key 不再被业务使用。</div>
          </div>
        </div>
      `
      this.$confirm(message, '删除确认', {
        dangerouslyUseHTMLString: true,
        customClass: 'redis-delete-message-box',
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        confirmButtonClass: 'el-button--danger',
        type: 'warning',
        closeOnClickModal: false
      }).then(async () => {
        const { data } = await http.delete('/sys/redis/key', { params: { key: row.key } })
        if (data.code === 0) {
          this.$message.success('删除成功')
          await this.refreshAll()
        }
      }).catch(() => {})
    },
    escapeHtml (value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    ttlText (ttl) {
      const value = Number(ttl)
      if (value === -1) return '永久'
      if (value === -2) return '不存在'
      if (!Number.isFinite(value)) return '-'
      return `${value}秒`
    },
    valueText (value) {
      if (value === undefined || value === null || value === '') return '-'
      if (typeof value === 'string') return value
      return JSON.stringify(value, null, 2)
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.stat-card {
  padding: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%);
  border: 1px solid #e5edf7;
}

.stat-label {
  color: #64748b;
  font-size: 13px;
}

.stat-value {
  margin-top: 8px;
  color: #1f2937;
  font-size: 24px;
  font-weight: 700;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.limit-alert {
  margin-bottom: 12px;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.detail-content {
  max-height: 420px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0 0;
}

.form-tip {
  color: #909399;
  font-size: 12px;
  line-height: 22px;
}

@media (max-width: 1200px) {
  .stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>

<style>
.redis-delete-message-box {
  width: 520px;
  border-radius: 14px;
}

.redis-delete-message-box .el-message-box__header {
  padding: 20px 22px 10px;
}

.redis-delete-message-box .el-message-box__title {
  font-weight: 700;
  color: #1f2937;
}

.redis-delete-message-box .el-message-box__content {
  padding: 10px 22px 18px;
}

.redis-delete-message-box .el-message-box__status {
  display: none;
}

.redis-delete-message-box .el-message-box__message {
  padding-left: 0;
}

.redis-delete-message-box .el-message-box__btns {
  padding: 0 22px 22px;
}

.redis-delete-message-box .el-button {
  border-radius: 8px;
  padding: 10px 18px;
}

.redis-delete-confirm {
  display: flex;
  gap: 14px;
  padding: 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, #fff7ed 0%, #fff1f2 100%);
  border: 1px solid #fed7aa;
}

.redis-delete-confirm__icon {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  font-weight: 800;
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
  box-shadow: 0 8px 18px rgba(239, 68, 68, 0.22);
}

.redis-delete-confirm__body {
  min-width: 0;
  flex: 1;
}

.redis-delete-confirm__title {
  margin-bottom: 8px;
  color: #111827;
  font-size: 15px;
  font-weight: 700;
}

.redis-delete-confirm__key {
  padding: 9px 10px;
  border-radius: 8px;
  color: #b91c1c;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(248, 113, 113, 0.28);
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-all;
}

.redis-delete-confirm__tip {
  margin-top: 9px;
  color: #7c2d12;
  font-size: 13px;
  line-height: 1.6;
}
</style>
